import { Button, Flex, FlexItem } from "@wordpress/components";
import { useSelect, withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment, useMemo } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React from "react";

import { useBlockStyle } from "../../hooks";

import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";

import { Icon } from "../../components";
import "../assets/sass/blocks/post-list/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "@blocks/utils";
import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		className,
		attributes: {
			layout,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			label,
			column,
			category,
			postCount,
			cssID,
			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,

			imageToggle,
			position,
			size,
			hoverAnimation,

			enableDate,
			enableReadTime,
			enableViewCount,
			metaPosition,
			separatorType,
			enableIcon,

			excerptLimit,
			enableExcerpt,

			enableReadMore,
			readMoreText,

			enableViewMore,
			viewMoreText,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreLink,
			viewButtonPosition,

			enablePagination,

			enableNumberList,
			numberLayout,
			numberPosition,
			enablePostsSeparator,

			hideOnDesktop,
			enableCategory,
			enableComment,
			categoryPosition,
			enableCategoryBorder,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "post-list",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const currentCategoryIndex = useMemo(() => {
		return categories
			? categories.findIndex((cat) => cat.id === parseInt(category))
			: -1; // Use -1 or another appropriate default value
	}, [category, categories]);

	const classNames = classnames(
		`mzb-post-list mzb-post-list-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	const classNames2 = classnames(
		`mzb-posts mzb-${layout}`,
		enableNumberList && "mzb-number-list",
		numberLayout && `mzb-number-list__${numberLayout}`,
		numberPosition && `mzb-number-list__${numberPosition}`,
		{
			[`mzb-${layout1AdvancedStyle}`]: layout === `layout-1`,
			[`mzb-${layout2AdvancedStyle}`]: layout === `layout-2`,
		},
		layout1AdvancedStyle === "layout-1-style-2" &&
			`mzb-post-col--${column || 4}`
	);

	const classNames3 = classnames(`mzb-post-heading mzb-${headingLayout}`, {
		[`mzb-${headingLayout1AdvancedStyle}`]:
			headingLayout === `heading-layout-1`,
		[`mzb-${headingLayout2AdvancedStyle}`]:
			headingLayout === `heading-layout-2`,
		[`mzb-${headingLayout3AdvancedStyle}`]:
			headingLayout === `heading-layout-3`,
		[`mzb-${headingLayout4AdvancedStyle}`]:
			headingLayout === `heading-layout-4`,
		[`mzb-${headingLayout5AdvancedStyle}`]:
			headingLayout === `heading-layout-5`,
		[`mzb-${headingLayout6AdvancedStyle}`]:
			headingLayout === `heading-layout-6`,
		[`mzb-${headingLayout7AdvancedStyle}`]:
			headingLayout === `heading-layout-7`,
		[`mzb-${headingLayout8AdvancedStyle}`]:
			headingLayout === `heading-layout-8`,
		[`mzb-${headingLayout9AdvancedStyle}`]:
			headingLayout === `heading-layout-9`,
	});

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / postCount)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames} id={cssID}>
				{enableHeading && (
					<div className={classNames3}>
						{enableHeading === true && (
							<h2 className="mzb-heading-text">{label}</h2>
						)}
						{viewButtonPosition === "top" && enableViewMore && (
							<div className="mzb-view-more">
								{/*eslint-disable-next-line react/jsx-no-target-blank */}
								<a
									href={"#"}
									target={
										viewMoreLink?.newTab
											? "_blank"
											: "_self"
									}
									rel={[
										viewMoreLink?.newTab
											? "noreferrer noopener"
											: "",
										viewMoreLink?.noFollow
											? "nofollow"
											: "",
									]
										.filter(Boolean)
										.join(" ")}
								>
									<p>{viewMoreText}</p>
									{enableViewMoreIcon && (
										<Icon
											type="frontendIcon"
											name={viewMoreIcon}
											size={24}
										/>
									)}
								</a>
							</div>
						)}
					</div>
				)}
				{
					<div className={classNames2}>
						<Fragment>
							{(posts || []).map((post, idx) => {
								const maxWords = excerptLimit;
								const excerpt = post?.excerpt?.rendered
									.split(" ")
									.slice(0, maxWords)
									.join(" ");

								return (
									<div
										className={cn(
											"mzb-post",
											`magazine-post--pos-${
												position || "left"
											}`,
											enablePostsSeparator &&
												`mzb-post-separator`
										)}
										key={idx}
									>
										{position === "left" &&
											post
												?.magazine_blocks_featured_image_url
												?.full?.[0] &&
											imageToggle && (
												<div
													className={`mzb-featured-image ${hoverAnimation} mzb-category-${categoryPosition}`}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full[0]
														}
													/>
													{(enableCategory ||
														enableComment) &&
														categoryPosition ===
															"in-image" && (
															<div
																className={
																	"mzb-post-meta" +
																	(enableCategoryBorder
																		? " mzb-post-border-enable"
																		: "")
																}
															>
																{enableCategory && (
																	<span
																		className="mzb-post-categories"
																		dangerouslySetInnerHTML={{
																			__html: post.magazine_blocks_category,
																		}}
																	/>
																)}
																{enableComment && (
																	<span className="comments-link">
																		<Icon
																			type="frontendIcon"
																			name="commentIcon"
																			size={
																				24
																			}
																		/>{" "}
																		{
																			post.magazine_blocks_comment
																		}
																	</span>
																)}
															</div>
														)}
												</div>
											)}
										<div className="mzb-post-content">
											{metaPosition === "top" && (
												<>
													{(enableDate ||
														enableReadTime ||
														enableViewCount) && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableDate && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}

															{enableReadTime ===
																true && (
																<span className="mzb-post-read-time">
																	{enableIcon ===
																		true && (
																		<Icon name="clock" />
																	)}
																	<span>
																		{
																			post?.magazineBlocksPostReadTime
																		}{" "}
																		min read
																	</span>
																</span>
															)}
															{enableViewCount ===
																true && (
																<span className="mzb-post-view-count">
																	{enableIcon ===
																		true && (
																		<Icon
																			name="eyeO"
																			fill="#fff"
																		/>
																	)}
																	<span>
																		{post?.magazineBlocksPostViewCount ||
																			0}{" "}
																		views
																	</span>
																</span>
															)}
														</div>
													)}

													{(enableCategory ||
														enableComment) &&
														categoryPosition ===
															"out-image" && (
															<div
																className={
																	"mzb-post-meta" +
																	(enableCategoryBorder
																		? " mzb-post-border-enable"
																		: "")
																}
															>
																{enableCategory && (
																	<span
																		className="mzb-post-categories"
																		dangerouslySetInnerHTML={{
																			__html: post.magazine_blocks_category,
																		}}
																	/>
																)}
																{enableComment && (
																	<span className="comments-link">
																		<Icon
																			type="frontendIcon"
																			name="commentIcon"
																			size={
																				24
																			}
																		/>{" "}
																		{
																			post.magazine_blocks_comment
																		}
																	</span>
																)}
															</div>
														)}

													<h3 className="mzb-post-title">
														<a href={"#"}>
															{
																post?.title
																	?.rendered
															}
														</a>
													</h3>
												</>
											)}

											{metaPosition === "bottom" && (
												<>
													{(enableCategory ||
														enableComment) &&
														categoryPosition ===
															"out-image" && (
															<div
																className={
																	"mzb-post-meta" +
																	(enableCategoryBorder
																		? " mzb-post-border-enable"
																		: "")
																}
															>
																{enableCategory && (
																	<span
																		className="mzb-post-categories"
																		dangerouslySetInnerHTML={{
																			__html: post.magazine_blocks_category,
																		}}
																	/>
																)}
																{enableComment && (
																	<span className="comments-link">
																		<Icon
																			type="frontendIcon"
																			name="commentIcon"
																			size={
																				24
																			}
																		/>{" "}
																		{
																			post.magazine_blocks_comment
																		}
																	</span>
																)}
															</div>
														)}
													<h3 className="mzb-post-title">
														<a href={"#"}>
															{
																post?.title
																	?.rendered
															}
														</a>
													</h3>

													{(enableDate ||
														enableReadTime ||
														enableViewCount) && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableDate && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
															{enableReadTime && (
																<span className="mzb-post-read-time">
																	{enableIcon ===
																		true && (
																		<Icon name="clock" />
																	)}
																	<span>
																		{
																			post?.magazineBlocksPostReadTime
																		}{" "}
																		min read
																	</span>
																</span>
															)}
															{enableViewCount && (
																<span className="mzb-post-view-count">
																	{enableIcon ===
																		true && (
																		<Icon
																			name="eyeO"
																			fill="#fff"
																		/>
																	)}
																	<span>
																		{post?.magazineBlocksPostViewCount ||
																			0}{" "}
																		views
																	</span>
																</span>
															)}
														</div>
													)}
												</>
											)}

											{(enableExcerpt ||
												enableReadMore) && (
												<div className="mzb-entry-content">
													{enableExcerpt && (
														<div
															className="mzb-entry-summary"
															dangerouslySetInnerHTML={{
																__html: excerpt,
															}}
														/>
													)}
													{enableReadMore && (
														<div className="mzb-read-more">
															<a href={"#"}>
																{readMoreText}
															</a>
														</div>
													)}
												</div>
											)}
										</div>
										{position === "right" &&
											post
												?.magazine_blocks_featured_image_url
												?.full?.[0] &&
											imageToggle && (
												<div
													className={`mzb-featured-image ${hoverAnimation}`}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full[0]
														}
													/>
												</div>
											)}
									</div>
								);
							})}
							{enablePagination && (
								<div className="mzb-pagination-numbers">
									{pagination}
								</div>
							)}
						</Fragment>
					</div>
				}
				{viewButtonPosition === "bottom" && enableViewMore && (
					<div className="mzb-view-more">
						{/*eslint-disable-next-line react/jsx-no-target-blank */}
						<a
							href={"#"}
							target={viewMoreLink?.newTab ? "_blank" : "_self"}
							rel={[
								viewMoreLink?.newTab
									? "noreferrer noopener"
									: "",
								viewMoreLink?.noFollow ? "nofollow" : "",
							]
								.filter(Boolean)
								.join(" ")}
						>
							<p>{viewMoreText}</p>
							{enableViewMoreIcon && (
								<Icon
									type="frontendIcon"
									name={viewMoreIcon}
									size={24}
								/>
							)}
						</a>
					</div>
				)}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			postCount,
			offset,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);

	const query: any = {
		per_page: postCount,
		offset: offset || 0,
		orderby: orderBy,
		order: orderType,
		author: authorName === "all" ? undefined : authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			query.categories = parseInt(category);
		}
		if (tag && tag !== "all") {
			query.tags = tag;
		}
		if (excludedCategory && excludedCategory !== "") {
			query.categories_exclude = excludedCategory;
		}
	}

	return {
		posts: getEntityRecords("postType", selectedPostType, query),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				categories:
					"all" === category || selectedPostType !== "post"
						? undefined
						: parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
